import { ReadonlyMat3 } from 'gl-matrix';
import { useIntl } from 'react-intl';
import {
    isPatientAligned,
    obliquityAnglesDeg,
} from '~/domain/viewport/frameRotation';
import { formatAngle } from '~/utils/obliquity';

/** A patient axis a frame turns about: left-right, anterior-posterior, superior-inferior. */
export type FrameAxis = 'lr' | 'ap' | 'si';

/** What an oblique frame reads as, in the user's language. */
export interface FrameRotationReadout {
    /**
     * The turn about each patient axis in degrees to one decimal, named by
     * its axis, e.g. "SI 14.8°"; undefined for an axis whose turn reads
     * 0.0° at that precision.
     */
    readonly angles: Readonly<Record<FrameAxis, string | undefined>>;
    /** What the angles mean, and what being oblique withholds. */
    readonly anglesTooltip: string;
    /** The action that returns the views to their anatomical planes. */
    readonly snapLabel: string;
}

// The `°` suffix lives in the `frameAxisAngle` message string itself so it
// is never dropped.
const ZERO_ANGLE = formatAngle(0);

/**
 * The readout of a frame rotation: the LR/AP/SI angles in degrees to one
 * decimal, each named by its axis, their explanation and the snap-back
 * label; or undefined while the frame is patient-aligned, when there is
 * nothing to tell the user. Every surface that shows a frame reads it
 * through this, so the angles are formatted one way.
 */
export function useFrameRotationReadout(
    rotation: ReadonlyMat3
): FrameRotationReadout | undefined {
    const intl = useIntl();
    if (isPatientAligned(rotation)) {
        return undefined;
    }
    const { lrDeg, apDeg, siDeg } = obliquityAnglesDeg(rotation);
    const angleAbout = (axis: FrameAxis, degrees: number) => {
        const angle = formatAngle(degrees);
        return angle === ZERO_ANGLE
            ? undefined
            : intl.formatMessage(
                  { id: 'frameAxisAngle' },
                  { axis: axis.toUpperCase(), angle }
              );
    };
    return {
        angles: {
            lr: angleAbout('lr', lrDeg),
            ap: angleAbout('ap', apDeg),
            si: angleAbout('si', siDeg),
        },
        anglesTooltip: intl.formatMessage({ id: 'obliquityAnglesTooltip' }),
        snapLabel: intl.formatMessage({ id: 'snapToOrthogonal' }),
    };
}
